import { sortBlocksByStablePosition } from '@/lib/blocks/tree';
import { readRecord } from '@/lib/prop-utils';
import { getBlockUiKey, type PageBlock } from '@/types/block';
import type { BlockDragData, DropIndicator, GapZoneData } from '../core';
import { isGapZoneData, isParentDropData } from '../core';
import {
  type DropValidationEventInput,
  type EventOverSnapshot,
  getEventActiveData,
  getEventActiveId,
} from './event-adapters';

const isBlockDragData = (data: unknown): data is BlockDragData => {
  const record = readRecord(data);
  return record?.type === 'block';
};

const getStableParentId = (
  block: PageBlock,
  stableIdByPersistedId: Map<string, string>,
): string | null => {
  if (!block.parentBlockId) {
    return null;
  }

  return stableIdByPersistedId.get(block.parentBlockId) ?? null;
};

const resolveCanonicalGapZoneIndicator = ({
  overData,
}: {
  overData: GapZoneData;
}): DropIndicator => {
  return { targetId: overData.blockId, edge: overData.edge };
};

const resolveCanonicalBlockIndicator = ({
  event,
  overId,
  blockMap,
  stableIdByPersistedId,
  allBlocks,
}: {
  event: DropValidationEventInput;
  overId: string;
  blockMap: Map<string, PageBlock>;
  stableIdByPersistedId: Map<string, string>;
  allBlocks: PageBlock[];
}): DropIndicator | null => {
  const activeId = getEventActiveId(event);
  if (activeId === overId) {
    return null;
  }

  const activeBlock = blockMap.get(activeId);
  const overBlock = blockMap.get(overId);
  if (!activeBlock || !overBlock) {
    return null;
  }

  const activeData = getEventActiveData<BlockDragData>(event);
  const activeParentId =
    activeData?.parentId ?? getStableParentId(activeBlock, stableIdByPersistedId);
  const overParentId = getStableParentId(overBlock, stableIdByPersistedId);
  if (activeParentId !== overParentId) {
    return { targetId: overId, edge: 'bottom' };
  }

  const siblings = sortBlocksByStablePosition(
    allBlocks.filter((block) => getStableParentId(block, stableIdByPersistedId) === overParentId),
  );
  const activeIndex = siblings.findIndex((block) => getBlockUiKey(block) === activeId);
  const overIndex = siblings.findIndex((block) => getBlockUiKey(block) === overId);
  if (activeIndex === -1 || overIndex === -1) {
    return { targetId: overId, edge: 'bottom' };
  }

  return { targetId: overId, edge: activeIndex < overIndex ? 'bottom' : 'top' };
};

export const resolveCanonicalDropIndicator = ({
  event,
  over,
  blockMap,
  stableIdByPersistedId,
  allBlocks,
}: {
  event: DropValidationEventInput;
  over: NonNullable<EventOverSnapshot>;
  blockMap: Map<string, PageBlock>;
  stableIdByPersistedId: Map<string, string>;
  allBlocks: PageBlock[];
}): DropIndicator | null => {
  const overData: unknown = over.data.current;
  if (isParentDropData(overData)) {
    return { targetId: String(over.id), edge: 'end' };
  }
  if (isGapZoneData(overData)) {
    return resolveCanonicalGapZoneIndicator({
      overData,
    });
  }
  if (isBlockDragData(overData)) {
    return resolveCanonicalBlockIndicator({
      event,
      overId: String(over.id),
      blockMap,
      stableIdByPersistedId,
      allBlocks,
    });
  }
  return null;
};
